/**
 * IntegrationsProposalPage — Visual Design Proposal
 *
 * This is a STATIC MOCKUP only. No real data. No API calls.
 * Purpose: Illustrate the proposed redesign for review and sign-off.
 *
 * Key changes illustrated:
 *  1. Card-based list replaces the table
 *  2. Single "Save All" commit model — no per-section Save buttons
 *  3. Unsaved changes guard (dirty state banner)
 *  4. Sheet locked from click-away when dirty
 *  5. Access Suite grouping (Financial + FinOps + Scheduler coupled)
 *  6. Instance reservation — "in use" labels on dropdowns
 *  7. Contextual inline "Suite" warning when products share a DB
 */

import { useState } from 'react';
import {
  Building2,
  Plus,
  Search,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Users,
  PoundSterling,
  FolderKanban,
  CalendarDays,
  ChevronRight,
  RefreshCw,
  Trash2,
  Loader2,
  Link2,
  Lock,
  AlertTriangle,
  X,
  Save,
  ArrowLeft,
  Globe,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Mock Data
// ---------------------------------------------------------------------------

interface MockInstance {
  id: string;
  name: string;
  reservedByCompanyId: string | null;
  reservedByCompanyName: string | null;
}

const MOCK_INSTANCES: MockInstance[] = [
  { id: 'inst-1', name: 'UK Main Instance',          reservedByCompanyId: 'company-1', reservedByCompanyName: 'UK Operations' },
  { id: 'inst-2', name: 'EU Central Instance',       reservedByCompanyId: 'company-2', reservedByCompanyName: 'EU Holdings' },
  { id: 'inst-3', name: 'APAC Instance',              reservedByCompanyId: null,        reservedByCompanyName: null },
  { id: 'inst-4', name: 'US East Instance',           reservedByCompanyId: null,        reservedByCompanyName: null },
  { id: 'inst-5', name: 'Shared Dev Instance',        reservedByCompanyId: null,        reservedByCompanyName: null },
];

type ConnectionStatus = 'connected' | 'error' | 'not_connected' | 'not_configured';

interface MockCompany {
  id: string;
  name: string;
  code: string;
  status: 'active' | 'inactive';
  userCount: number;
  financial: { product: string | null; instance: string | null; status: ConnectionStatus; lastSync: string | null };
  hr: { product: string | null; instance: string | null; status: ConnectionStatus; lastSync: string | null };
  project: { product: string | null; instance: string | null; status: ConnectionStatus; lastSync: string | null };
  planner: { product: string | null; instance: string | null; status: ConnectionStatus; lastSync: string | null };
}

const MOCK_COMPANIES: MockCompany[] = [
  {
    id: 'company-1', name: 'UK Operations', code: 'UK-OPS', status: 'active', userCount: 14,
    financial: { product: 'Access Financials',            instance: 'UK Main Instance',   status: 'connected',     lastSync: '2025-06-01T09:00:00Z' },
    hr:        { product: 'People HR',                    instance: 'UK Main Instance',   status: 'connected',     lastSync: '2025-06-01T09:05:00Z' },
    project:   { product: 'Access Financial Operations',  instance: 'UK Main Instance',   status: 'connected',     lastSync: '2025-06-01T09:10:00Z' },
    planner:   { product: 'Resource Scheduler',           instance: 'UK Main Instance',   status: 'connected',     lastSync: '2025-06-01T09:15:00Z' },
  },
  {
    id: 'company-2', name: 'EU Holdings', code: 'EU-HOLD', status: 'active', userCount: 8,
    financial: { product: 'Access Financials',            instance: 'EU Central Instance', status: 'connected',    lastSync: '2025-05-31T14:00:00Z' },
    hr:        { product: 'BambooHR',                     instance: null,                  status: 'error',        lastSync: null },
    project:   { product: 'Access Projects',              instance: 'EU Central Instance', status: 'connected',    lastSync: '2025-05-31T14:05:00Z' },
    planner:   { product: null,                           instance: null,                  status: 'not_configured', lastSync: null },
  },
  {
    id: 'company-3', name: 'North America Ltd', code: 'NA-LTD', status: 'active', userCount: 5,
    financial: { product: 'Xero',                         instance: null,                  status: 'not_connected', lastSync: null },
    hr:        { product: null,                           instance: null,                  status: 'not_configured', lastSync: null },
    project:   { product: null,                           instance: null,                  status: 'not_configured', lastSync: null },
    planner:   { product: null,                           instance: null,                  status: 'not_configured', lastSync: null },
  },
  {
    id: 'company-4', name: 'APAC Division', code: 'APAC', status: 'active', userCount: 3,
    financial: { product: null,                           instance: null,                  status: 'not_configured', lastSync: null },
    hr:        { product: null,                           instance: null,                  status: 'not_configured', lastSync: null },
    project:   { product: null,                           instance: null,                  status: 'not_configured', lastSync: null },
    planner:   { product: null,                           instance: null,                  status: 'not_configured', lastSync: null },
  },
  {
    id: 'company-5', name: 'Legacy EMEA', code: 'EMEA', status: 'inactive', userCount: 0,
    financial: { product: 'Access Dimensions',            instance: null,                  status: 'not_connected', lastSync: null },
    hr:        { product: null,                           instance: null,                  status: 'not_configured', lastSync: null },
    project:   { product: null,                           instance: null,                  status: 'not_configured', lastSync: null },
    planner:   { product: null,                           instance: null,                  status: 'not_configured', lastSync: null },
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getOverallHealth(company: MockCompany): 'all-good' | 'errors' | 'warnings' | 'not-configured' {
  const all = [company.financial, company.hr, company.project, company.planner];
  const configured = all.filter(i => i.status !== 'not_configured');
  if (configured.length === 0) return 'not-configured';
  if (configured.some(i => i.status === 'error')) return 'errors';
  if (configured.some(i => i.status === 'not_connected')) return 'warnings';
  return 'all-good';
}

function formatSync(iso: string | null) {
  if (!iso) return 'Never synced';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatusDot({ status }: { status: ConnectionStatus }) {
  if (status === 'connected')    return <span className="h-2 w-2 rounded-full bg-success-500 flex-shrink-0 ring-2 ring-success-500/20" />;
  if (status === 'error')        return <span className="h-2 w-2 rounded-full bg-destructive flex-shrink-0 ring-2 ring-destructive/20" />;
  if (status === 'not_connected') return <span className="h-2 w-2 rounded-full bg-warning-500 flex-shrink-0 ring-2 ring-warning-500/20" />;
  return                                <span className="h-2 w-2 rounded-full bg-muted-foreground/30 flex-shrink-0 ring-2 ring-muted/40" />;
}

function IntegrationPill({ label, icon, product, status }: { label: string; icon: React.ReactNode; product: string | null; status: ConnectionStatus }) {
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <span className="text-muted-foreground/70 flex-shrink-0">{icon}</span>
      {product ? (
        <div className="flex items-center gap-1.5 min-w-0">
          <StatusDot status={status} />
          <span className="text-xs font-medium truncate">{product}</span>
        </div>
      ) : (
        <span className="text-xs text-muted-foreground/50 italic">{label} not set</span>
      )}
    </div>
  );
}

function HealthIndicator({ company }: { company: MockCompany }) {
  const health = getOverallHealth(company);
  if (health === 'all-good') return (
    <div className="flex items-center gap-1 text-success-700 bg-success-50 border border-success-200 rounded-full px-2 py-0.5">
      <CheckCircle2 className="h-3 w-3" />
      <span className="text-[10px] font-semibold">All Connected</span>
    </div>
  );
  if (health === 'errors') return (
    <div className="flex items-center gap-1 text-destructive bg-destructive/5 border border-destructive/20 rounded-full px-2 py-0.5">
      <XCircle className="h-3 w-3" />
      <span className="text-[10px] font-semibold">Has Errors</span>
    </div>
  );
  if (health === 'warnings') return (
    <div className="flex items-center gap-1 text-warning-700 bg-warning-50 border border-warning-200 rounded-full px-2 py-0.5">
      <AlertCircle className="h-3 w-3" />
      <span className="text-[10px] font-semibold">Not Connected</span>
    </div>
  );
  return (
    <div className="flex items-center gap-1 text-muted-foreground bg-muted/60 border rounded-full px-2 py-0.5">
      <Globe className="h-3 w-3" />
      <span className="text-[10px] font-semibold">Not Configured</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Company Card — replaces the table row
// ---------------------------------------------------------------------------

function CompanyCard({ company, isSelected, onClick }: {
  company: MockCompany;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={cn(
        'group relative flex flex-col gap-3 rounded-lg border bg-card p-4 cursor-pointer transition-all duration-150',
        'hover:border-primary/30 hover:shadow-sm',
        isSelected && 'border-primary/60 bg-primary/5 shadow-sm',
        company.status === 'inactive' && 'opacity-60'
      )}
    >
      {/* Top row: Company identity + health + chevron */}
      <div className="flex items-start gap-3 min-w-0">
        <div className={cn(
          'flex h-9 w-9 items-center justify-center rounded-md flex-shrink-0 text-sm font-bold',
          company.status === 'active' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
        )}>
          {company.code.slice(0, 2)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm leading-tight">{company.name}</span>
            {company.status === 'inactive' && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Inactive</Badge>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-muted-foreground font-mono">{company.code}</span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" />
              {company.userCount} {company.userCount === 1 ? 'user' : 'users'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <HealthIndicator company={company} />
          <ChevronRight className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-150',
            isSelected && 'rotate-90 text-primary'
          )} />
        </div>
      </div>

      {/* Integration grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 pl-12">
        <IntegrationPill label="Financial" icon={<PoundSterling className="h-3 w-3" />} product={company.financial.product} status={company.financial.status} />
        <IntegrationPill label="HR"        icon={<Users         className="h-3 w-3" />} product={company.hr.product}        status={company.hr.status} />
        <IntegrationPill label="Project"   icon={<FolderKanban  className="h-3 w-3" />} product={company.project.product}   status={company.project.status} />
        <IntegrationPill label="Planner"   icon={<CalendarDays  className="h-3 w-3" />} product={company.planner.product}   status={company.planner.status} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Instance Select — shows reserved instances as disabled with "in use by X"
// ---------------------------------------------------------------------------

function InstanceSelect({
  value,
  onChange,
  currentCompanyId,
  disabled = false,
  placeholder = 'Select instance…',
}: {
  value: string;
  onChange: (v: string) => void;
  currentCompanyId: string;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-9 text-sm">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {MOCK_INSTANCES.map((inst) => {
          const isReservedByOther =
            inst.reservedByCompanyId !== null &&
            inst.reservedByCompanyId !== currentCompanyId;
          return (
            <SelectItem
              key={inst.id}
              value={inst.id}
              disabled={isReservedByOther}
              className={cn(isReservedByOther && 'opacity-50 cursor-not-allowed')}
            >
              <div className="flex items-center justify-between gap-3 w-full">
                <span>{inst.name}</span>
                {isReservedByOther && (
                  <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                    <Lock className="h-2.5 w-2.5" />
                    In use by {inst.reservedByCompanyName}
                  </span>
                )}
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Integration Row inside Sheet — simplified, no per-row Save
// ---------------------------------------------------------------------------

function IntegrationRow({
  label,
  icon,
  product,
  instance,
  status,
  lastSync,
  isSuiteLinked = false,
  suiteNote,
  onProductChange,
  onInstanceChange,
  onRemove,
  products,
  companyId,
  readOnly,
}: {
  label: string;
  icon: React.ReactNode;
  product: string | null;
  instance: string | null;
  status: ConnectionStatus;
  lastSync: string | null;
  isSuiteLinked?: boolean;
  suiteNote?: string;
  onProductChange: (v: string) => void;
  onInstanceChange: (v: string) => void;
  onRemove: () => void;
  products: { value: string; label: string }[];
  companyId: string;
  readOnly: boolean;
}) {
  return (
    <div className={cn(
      'rounded-lg border bg-card p-4 space-y-3',
      isSuiteLinked && 'border-primary/20 bg-primary/5'
    )}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={cn('text-muted-foreground', isSuiteLinked && 'text-primary/70')}>{icon}</span>
          <span className="text-sm font-semibold">{label}</span>
          {isSuiteLinked && (
            <span className="flex items-center gap-1 text-[10px] text-primary font-medium bg-primary/10 px-1.5 py-0.5 rounded-full">
              <Link2 className="h-2.5 w-2.5" />
              Linked to Suite
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {product && (
            <>
              {status === 'connected' && <span className="flex items-center gap-1 text-xs text-success-700"><CheckCircle2 className="h-3 w-3" />Connected</span>}
              {status === 'error'     && <span className="flex items-center gap-1 text-xs text-destructive"><XCircle className="h-3 w-3" />Error</span>}
              {status === 'not_connected' && <span className="flex items-center gap-1 text-xs text-warning-700"><AlertCircle className="h-3 w-3" />Not Connected</span>}
            </>
          )}
          {product && !readOnly && !isSuiteLinked && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={onRemove}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Suite lock note */}
      {isSuiteLinked && suiteNote && (
        <div className="flex items-start gap-2 text-xs text-primary/80 bg-primary/5 rounded-md px-3 py-2 border border-primary/15">
          <Link2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>{suiteNote}</span>
        </div>
      )}

      {/* Product selector */}
      {!isSuiteLinked && (
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Product</Label>
          {readOnly ? (
            <p className="text-sm py-1">{product ?? <span className="text-muted-foreground italic">Not configured</span>}</p>
          ) : (
            <Select value={product ?? ''} onValueChange={onProductChange}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select a product…" />
              </SelectTrigger>
              <SelectContent>
                {products.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Instance selector — shown when product is selected and it's an Access product */}
      {product && product.startsWith('Access') && !isSuiteLinked && (
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Instance</Label>
          {readOnly ? (
            <p className="text-sm py-1">{instance ?? <span className="text-muted-foreground italic">Not selected</span>}</p>
          ) : (
            <InstanceSelect
              value={instance ?? ''}
              onChange={onInstanceChange}
              currentCompanyId={companyId}
            />
          )}
        </div>
      )}

      {/* Instance display for linked rows */}
      {isSuiteLinked && instance && (
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Instance</Label>
          <div className="flex items-center gap-2 h-9 rounded-md bg-muted/50 border border-dashed px-3 text-sm text-muted-foreground">
            <Lock className="h-3.5 w-3.5 flex-shrink-0" />
            <span>{instance}</span>
          </div>
        </div>
      )}

      {/* Last sync */}
      {lastSync && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <RefreshCw className="h-3 w-3 flex-shrink-0" />
          Last synced: <span className="font-medium text-foreground">{formatSync(lastSync)}</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The Sheet: Detail & Integrations
// ---------------------------------------------------------------------------

function CompanyDetailSheet({
  company,
  open,
  onOpenChange,
}: {
  company: MockCompany | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  // Simulated dirty state
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [financialProduct, setFinancialProduct] = useState(company?.financial.product ?? '');
  const [hrProduct, setHrProduct] = useState(company?.hr.product ?? '');
  const [projectProduct, setProjectProduct] = useState(company?.project.product ?? '');

  if (!company) return null;

  // Access Suite products that share the Financial instance
  const isAccessSuite = financialProduct === 'Access Financials' || financialProduct === 'Access Dimensions';
  const suiteInstance = company.financial.instance;

  function handleRequestClose() {
    if (isDirty) {
      setShowDiscardDialog(true);
    } else {
      onOpenChange(false);
    }
  }

  function handleSimulateDirty() {
    setIsDirty(true);
  }

  function handleSaveAll() {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setIsDirty(false);
      onOpenChange(false);
    }, 1200);
  }

  function handleDiscard() {
    setShowDiscardDialog(false);
    setIsDirty(false);
    onOpenChange(false);
  }

  const FINANCIAL_PRODUCTS = [
    { value: 'Access Financials',  label: 'Access Financials' },
    { value: 'Access Dimensions',  label: 'Access Dimensions' },
    { value: 'Xero',               label: 'Xero' },
    { value: 'Sage',               label: 'Sage' },
    { value: 'QuickBooks',         label: 'QuickBooks' },
  ];
  const HR_PRODUCTS = [
    { value: 'People HR',  label: 'People HR' },
    { value: 'BambooHR',   label: 'BambooHR' },
    { value: 'Workday',    label: 'Workday' },
    { value: 'ADP',        label: 'ADP' },
    { value: 'Paycircle',  label: 'Paycircle' },
  ];
  const PROJECT_PRODUCTS = [
    { value: 'Access Projects',              label: 'Access Projects' },
    { value: 'Access Financial Operations',  label: 'Access Financial Operations' },
    { value: 'Access FocalPoint',            label: 'Access FocalPoint' },
  ];
  const PLANNER_PRODUCTS = [
    { value: 'Resource Scheduler', label: 'Resource Scheduler' },
    { value: 'Access Planner',     label: 'Access Planner' },
  ];

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(v) => {
          if (!v) handleRequestClose();
        }}
      >
        {/* Prevent click-away dismissal when dirty — handled manually */}
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl p-0 flex flex-col overflow-hidden"
          onInteractOutside={(e) => {
            if (isDirty) e.preventDefault();
          }}
        >
          {/* Header */}
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-md flex-shrink-0 text-sm font-bold',
                  'bg-primary/10 text-primary'
                )}>
                  {company.code.slice(0, 2)}
                </div>
                <div className="min-w-0">
                  <SheetTitle className="text-base leading-tight">{company.name}</SheetTitle>
                  <SheetDescription className="text-xs font-mono">{company.code}</SheetDescription>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 flex-shrink-0"
                onClick={handleRequestClose}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>

          {/* Dirty-state banner */}
          {isDirty && (
            <div className="flex items-center justify-between gap-3 px-6 py-2.5 bg-warning-50 border-b border-warning-200 shrink-0">
              <div className="flex items-center gap-2 text-sm text-warning-800">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 text-warning-600" />
                <span className="font-medium">You have unsaved changes</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-warning-700 hover:text-warning-900 hover:bg-warning-100"
                onClick={() => setShowDiscardDialog(true)}
              >
                Discard
              </Button>
            </div>
          )}

          {/* Tabs */}
          <Tabs defaultValue="integrations" className="flex flex-col flex-1 min-h-0">
            <div className="px-6 pt-3 pb-0 border-b shrink-0">
              <TabsList className="h-9 w-full rounded-none bg-transparent p-0 gap-0 justify-start">
                <TabsTrigger
                  value="details"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
                >
                  Details
                </TabsTrigger>
                <TabsTrigger
                  value="integrations"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
                >
                  Integrations
                </TabsTrigger>
                <TabsTrigger
                  value="users"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
                >
                  Users
                  <span className="ml-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-medium h-4 min-w-4 px-1 bg-muted text-muted-foreground">
                    {company.userCount}
                  </span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* ── Tab: Details ── */}
            <TabsContent value="details" className="flex-1 overflow-y-auto mt-0">
              <div className="px-6 py-5 space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="company-name">Company Name</Label>
                  <Input id="company-name" defaultValue={company.name} onChange={handleSimulateDirty} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="company-code">Company Code</Label>
                  <Input id="company-code" defaultValue={company.code} className="font-mono uppercase" onChange={handleSimulateDirty} />
                  <p className="text-xs text-muted-foreground">Uppercase letters, numbers and hyphens only</p>
                </div>
                <div className="flex items-center justify-between py-1">
                  <div>
                    <Label className="text-sm font-medium">Active</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Inactive companies are hidden from the company switcher</p>
                  </div>
                  <Switch defaultChecked={company.status === 'active'} onCheckedChange={handleSimulateDirty} />
                </div>
              </div>
            </TabsContent>

            {/* ── Tab: Integrations ── */}
            <TabsContent value="integrations" className="flex-1 overflow-y-auto mt-0">
              <div className="px-6 py-5 space-y-4">

                {/* Suite coupling notice */}
                {isAccessSuite && (
                  <div className="flex items-start gap-2.5 rounded-lg bg-primary/5 border border-primary/20 px-4 py-3">
                    <Link2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium text-primary">Access Suite Active</p>
                      <p className="text-xs text-muted-foreground">
                        <strong>Access Financials</strong>, <strong>Financial Operations</strong> and <strong>Resource Scheduler</strong> share the same database instance.
                        Changing the Financial instance will automatically update the linked integrations.
                      </p>
                    </div>
                  </div>
                )}

                {/* Financial */}
                <IntegrationRow
                  label="Financial"
                  icon={<PoundSterling className="h-4 w-4" />}
                  product={financialProduct || company.financial.product}
                  instance={company.financial.instance}
                  status={company.financial.status}
                  lastSync={company.financial.lastSync}
                  onProductChange={(v) => { setFinancialProduct(v); handleSimulateDirty(); }}
                  onInstanceChange={handleSimulateDirty}
                  onRemove={handleSimulateDirty}
                  products={FINANCIAL_PRODUCTS}
                  companyId={company.id}
                  readOnly={false}
                />

                {/* FinOps — linked to Financial when Access Suite */}
                <IntegrationRow
                  label="Financial Operations"
                  icon={<FolderKanban className="h-4 w-4" />}
                  product={isAccessSuite ? 'Access Financial Operations' : (projectProduct || company.project.product)}
                  instance={isAccessSuite ? suiteInstance : company.project.instance}
                  status={isAccessSuite ? company.financial.status : company.project.status}
                  lastSync={company.project.lastSync}
                  isSuiteLinked={isAccessSuite}
                  suiteNote={isAccessSuite
                    ? `This integration shares the Financial instance (${suiteInstance ?? 'not set'}) and is configured automatically.`
                    : undefined}
                  onProductChange={(v) => { setProjectProduct(v); handleSimulateDirty(); }}
                  onInstanceChange={handleSimulateDirty}
                  onRemove={handleSimulateDirty}
                  products={PROJECT_PRODUCTS}
                  companyId={company.id}
                  readOnly={false}
                />

                {/* Scheduler — linked to Financial when Access Suite */}
                <IntegrationRow
                  label="Resource Planner"
                  icon={<CalendarDays className="h-4 w-4" />}
                  product={isAccessSuite ? 'Resource Scheduler' : company.planner.product}
                  instance={isAccessSuite ? suiteInstance : company.planner.instance}
                  status={isAccessSuite ? company.financial.status : company.planner.status}
                  lastSync={company.planner.lastSync}
                  isSuiteLinked={isAccessSuite}
                  suiteNote={isAccessSuite
                    ? `Resource Scheduler is part of the Access Suite and inherits the Financial instance (${suiteInstance ?? 'not set'}).`
                    : undefined}
                  onProductChange={() => handleSimulateDirty()}
                  onInstanceChange={handleSimulateDirty}
                  onRemove={handleSimulateDirty}
                  products={PLANNER_PRODUCTS}
                  companyId={company.id}
                  readOnly={false}
                />

                {/* HR — always independent */}
                <IntegrationRow
                  label="HR"
                  icon={<Users className="h-4 w-4" />}
                  product={hrProduct || company.hr.product}
                  instance={company.hr.instance}
                  status={company.hr.status}
                  lastSync={company.hr.lastSync}
                  onProductChange={(v) => { setHrProduct(v); handleSimulateDirty(); }}
                  onInstanceChange={handleSimulateDirty}
                  onRemove={handleSimulateDirty}
                  products={HR_PRODUCTS}
                  companyId={company.id}
                  readOnly={false}
                />

              </div>
            </TabsContent>

            {/* ── Tab: Users ── */}
            <TabsContent value="users" className="flex-1 overflow-y-auto mt-0">
              <div className="px-6 py-5">
                <p className="text-sm text-muted-foreground">Users with access to this company are managed here. (Existing pattern — no changes proposed.)</p>
              </div>
            </TabsContent>
          </Tabs>

          {/* ── Sticky footer with single Save ── */}
          <div className="px-6 py-4 border-t bg-card shrink-0">
            <div className="flex items-center gap-3">
              <Button
                className="flex-1"
                onClick={handleSaveAll}
                disabled={isSaving || !isDirty}
              >
                {isSaving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" />Save All Changes</>
                )}
              </Button>
              <Button variant="outline" onClick={handleRequestClose} disabled={isSaving}>
                Cancel
              </Button>
            </div>
            {isDirty && (
              <p className="text-xs text-muted-foreground text-center mt-2">
                Changes to Financial, Operations and Planner will all be committed together.
              </p>
            )}
          </div>

        </SheetContent>
      </Sheet>

      {/* Discard confirmation */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to <strong>{company.name}</strong>. Leaving now will discard them permanently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscard}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Proposal Page
// ---------------------------------------------------------------------------

export default function IntegrationsProposalPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [selectedCompany, setSelectedCompany] = useState<MockCompany | null>(null);

  const filtered = MOCK_COMPANIES.filter((c) => {
    const matchesSearch =
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.code.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Proposal banner */}
      <div className="flex items-start gap-3 rounded-lg bg-primary/5 border border-primary/20 px-5 py-4">
        <AlertTriangle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
        <div className="space-y-0.5">
          <p className="text-sm font-semibold text-primary">Design Proposal — Static Mockup Only</p>
          <p className="text-xs text-muted-foreground">
            This screen illustrates proposed UX changes: <strong>card list</strong> replacing the table, <strong>single Save button</strong>,
            <strong> click-away protection</strong>, <strong>Access Suite grouping</strong>, and <strong>instance reservation</strong>.
            No real data is used. Click any company card to open the new Sheet design.
          </p>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            Integrations
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{filtered.length} {filtered.length === 1 ? 'company' : 'companies'}</p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Company
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search by name or code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Card list */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Building2 className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <p className="font-medium text-muted-foreground">No companies found</p>
            {search && (
              <button className="text-xs text-primary underline mt-1" onClick={() => setSearch('')}>
                Clear search
              </button>
            )}
          </div>
        ) : (
          filtered.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              isSelected={selectedCompany?.id === company.id}
              onClick={() => setSelectedCompany(company)}
            />
          ))
        )}
      </div>

      {/* Sheet */}
      <CompanyDetailSheet
        company={selectedCompany}
        open={!!selectedCompany}
        onOpenChange={(v) => { if (!v) setSelectedCompany(null); }}
      />
    </div>
  );
}
